import React, { useState, useEffect } from 'react';
import { User, Role, usersAPI, CreateUserInput, UpdateUserInput } from '../api/client';
import { getRoleLabel } from '../context/AuthContext';
import './UserModal.css';

interface UserModalProps {
  user: User | null; // null = creating new user
  onClose: () => void;
  onSave: (user: User) => void;
  currentUserId: number;
}

const UserModal: React.FC<UserModalProps> = ({ user, onClose, onSave, currentUserId }) => {
  const isEditing = user !== null;
  const isSelf = user?.id === currentUserId;

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'user' as Role,
    email: '',
    display_name: '',
    is_active: true,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordFields, setShowPasswordFields] = useState(!isEditing);

  // Initialize form data when editing, reset when creating new
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        password: '',
        confirmPassword: '',
        role: user.role,
        email: user.email || '',
        display_name: user.display_name || '',
        is_active: user.is_active,
      });
    } else {
      // Reset form for new user
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        role: 'user',
        email: '',
        display_name: '',
        is_active: true,
      });
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
  };

  const validate = (): boolean => {
    // Username validation
    if (!formData.username || formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, underscores, and hyphens');
      return false;
    }

    // Password validation (required for new users, optional for edit)
    if (!isEditing || showPasswordFields) {
      if (!isEditing && !formData.password) {
        setError('Password is required');
        return false;
      }

      if (formData.password && formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }

      if (formData.password && formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    // Role protection for supervisor
    if (user?.username === 'supervisor' && formData.role !== 'admin') {
      setError('Cannot change supervisor role');
      return false;
    }

    // Cannot deactivate supervisor
    if (user?.username === 'supervisor' && !formData.is_active) {
      setError('Cannot deactivate supervisor');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let result: User;

      if (isEditing && user) {
        // Update existing user
        const updateData: UpdateUserInput = {
          username: formData.username !== user.username ? formData.username : undefined,
          role: formData.role !== user.role ? formData.role : undefined,
          email: formData.email !== (user.email || '') ? (formData.email || null) : undefined,
          display_name: formData.display_name !== (user.display_name || '')
            ? (formData.display_name || null)
            : undefined,
          is_active: formData.is_active !== user.is_active ? formData.is_active : undefined,
        };

        // Remove undefined values
        Object.keys(updateData).forEach((key) => {
          if (updateData[key as keyof UpdateUserInput] === undefined) {
            delete updateData[key as keyof UpdateUserInput];
          }
        });

        // Update user info
        if (Object.keys(updateData).length > 0) {
          const response = await usersAPI.update(user.id, updateData);
          result = response.user;
        } else {
          result = user;
        }

        // Update password separately if changed
        if (showPasswordFields && formData.password) {
          await usersAPI.changePassword(user.id, formData.password);
        }
      } else {
        // Create new user
        const createData: CreateUserInput = {
          username: formData.username,
          password: formData.password,
          role: formData.role,
          email: formData.email || undefined,
          display_name: formData.display_name || undefined,
        };

        const response = await usersAPI.create(createData);
        result = response.user;
      }

      onSave(result);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit User' : 'Add User'}</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={user?.username === 'supervisor'}
              required
              minLength={3}
              maxLength={50}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_name">Display Name</label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              maxLength={100}
              placeholder="Optional friendly name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              maxLength={255}
              placeholder="Optional contact email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={user?.username === 'supervisor'}
              required
            >
              <option value="admin">{getRoleLabel('admin')}</option>
              <option value="power_user">{getRoleLabel('power_user')}</option>
              <option value="user">{getRoleLabel('user')}</option>
            </select>
            <span className="form-help">
              {formData.role === 'admin' && 'Full access, can manage users'}
              {formData.role === 'power_user' && 'Most features, cannot manage users'}
              {formData.role === 'user' && 'Basic access, own sessions only'}
            </span>
          </div>

          {isEditing && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="showPasswordFields"
                  checked={showPasswordFields}
                  onChange={(e) => setShowPasswordFields(e.target.checked)}
                />
                Change password
              </label>
            </div>
          )}

          {(!isEditing || showPasswordFields) && (
            <>
              <div className="form-group">
                <label htmlFor="password">
                  Password {!isEditing && '*'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEditing}
                  minLength={8}
                  placeholder={isEditing ? 'Leave blank to keep current' : 'Minimum 8 characters'}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">
                  Confirm Password {!isEditing && formData.password && '*'}
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={!isEditing || !!formData.password}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                />
              </div>
            </>
          )}

          {isEditing && !isSelf && user?.username !== 'supervisor' && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                Active
              </label>
              <span className="form-help">Inactive users cannot log in</span>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
